import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as EventTicketPackagesActions from './event-ticket-packages.actions'
import { EventTicketPackageEntity } from './event-ticket-packages.models'
import { HttpErrorResponse } from '@angular/common/http'
import { environment } from 'apps/gb-front/src/environments/environment'

export const EVENT_TICKET_PACKAGES_FEATURE_KEY = 'eventTicketPackages'

export interface EventTicketPackagesState extends EntityState<EventTicketPackageEntity> {
  selectedId?: string | number // which EventTicketPackages record has been selected
  count: number
  order: string
  stale: number[]
  error?: HttpErrorResponse | null // last known error (if any)
}

export interface EventTicketPackagesPartialState {
  readonly [EVENT_TICKET_PACKAGES_FEATURE_KEY]: EventTicketPackagesState
}

export const eventTicketPackagesAdapter: EntityAdapter<EventTicketPackageEntity> =
  createEntityAdapter<EventTicketPackageEntity>({
    selectId: (eventTicketPackage: EventTicketPackageEntity) => eventTicketPackage.uuid,
    sortComparer: (a: EventTicketPackageEntity, b: EventTicketPackageEntity) => (a.order < b.order ? 0 : 1),
  })

export const initialEventTicketPackagesState: EventTicketPackagesState = eventTicketPackagesAdapter.getInitialState({
  // set initial required properties
  count: 0,
  order: '',
  stale: [],
})

const eventTicketPackagesReducer = createReducer(
  initialEventTicketPackagesState,
  on(EventTicketPackagesActions.loadEventTicketPackages, (state) => ({
    ...state,
    error: null,
  })),
  on(EventTicketPackagesActions.upsertEventTicketPackages, (state, { eventTicketPackages }) =>
    eventTicketPackagesAdapter.upsertMany(getListItems(eventTicketPackages), state),
  ),
  on(EventTicketPackagesActions.loadEventTicketPackagesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(EventTicketPackagesActions.addEventTicketPackage, (state, action) =>
    eventTicketPackagesAdapter.addOne(action.eventTicketPackage, state),
  ),
  on(EventTicketPackagesActions.updateEventTicketPackage, (state, action) =>
    eventTicketPackagesAdapter.updateOne(action.update, state),
  ),
  on(EventTicketPackagesActions.deleteEventTicketPackage, (state, action) =>
    eventTicketPackagesAdapter.removeOne(action.uuid, state),
  ),
  on(EventTicketPackagesActions.updateCount, (state, action) => ({
    ...state,
    count: action.count,
  })),
  on(EventTicketPackagesActions.updateOrderHash, (state, action) => ({
    ...state,
    order: action.order,
  })),
  on(EventTicketPackagesActions.selectEventTicketPackage, (state, { uuid }) => ({
    ...state,
    selectedId: uuid,
  })),
  on(EventTicketPackagesActions.clearSelectedEventTicketPackage, (state) => ({
    ...state,
    selectedId: initialEventTicketPackagesState.selectedId,
  })),
  on(EventTicketPackagesActions.setAllEventTicketPackagesStale, (state) => ({
    ...state,
    stale: getPages(state.ids),
  })),
  on(EventTicketPackagesActions.removePageFromStale, (state, action) => ({
    ...state,
    // remove resynced page from stale pages
    stale: state.stale.filter((page) => page !== action.page),
  })),
  on(EventTicketPackagesActions.clearEventTicketPackages, (state) => ({
    ...state,
    ...eventTicketPackagesAdapter.removeAll(state),
  })),
)

/**
 * Adds notListedLoaded = false flag to each ticket package entity.
 * Required to update flag for ticket packages previously loaded but
 * not from list (i.e. directly accessed via URL)
 */
export const getListItems = (items: EventTicketPackageEntity[]) =>
  items.map((item) => ({ ...item, notListLoaded: false }))

export const getPages = (arr: string[] | number[]) => {
  return arr
    .map((_, index: number) => (index % environment.itemsPageSize === 0 ? index : null))
    .filter((i): i is number => i !== null)
}

export function reducer(state: EventTicketPackagesState | undefined, action: Action) {
  return eventTicketPackagesReducer(state, action)
}
